import { Injectable } from '@angular/core';
import { CachedHttpService } from './cached-http.service';
import { RouterService } from './router.service';
import { Session } from '../types/session';
import { UserSessionService } from './user-session.service';
import { AppConfigService } from '../../app.config';

@Injectable({
    providedIn: 'root',
})
export class SessionService {
    private readonly sessionKey = 'session';
    private readonly url: string;

    /**
     * Sets the url for the session endpoints.
     * @param cachedHttp A caching http service.
     * @param userSession Service providing the session$ observable.
     * @param configService Service providing the app configs.
     * @param route A service to build the url.
     */
    constructor(
        private cachedHttp: CachedHttpService,
        private userSession: UserSessionService,
        private configService: AppConfigService,
        route: RouterService,
    ) {
        this.url = route.getApiUrl('sessions', true);
    }

    /**
     * @see AppConfigService.getConfig
     */
    get configs() {
        return this.configService.getConfig();
    }

    /**
     * Gets all sessions from the API.
     * @param invalidate Flag to invalidate the cache.
     */
    public getSessions(invalidate = false) {
        return this.cachedHttp.get<Session[]>(this.url, invalidate);
    }

    /**
     * Initializes the current session from local storage if possible or from the api using the configured default
     * session if not.
     * @param invalidate Flag to invalidate the cache.
     */
    public initSession(invalidate = false) {
        const sessionString = localStorage.getItem(this.sessionKey);
        if (sessionString) {
            const session = JSON.parse(sessionString);
            this.userSession.setSession(session);
        } else {
            this.cachedHttp
                .get<Session>(
                    `${this.url}/${this.configs.defaultSession}`,
                    invalidate,
                )
                .subscribe((session) => this.userSession.setSession(session));
        }
    }
}
